<div class="scv2-section">
  <div class="scv2-section-head scv2-rules-head">
    <div>
      <h2 class="scv2-h2">Shipping zones</h2>
      <p class="scv2-sub">
        Each zone sets different rates for a region. A customer sees the zone that matches their address at checkout.
      </p>
    </div>
    <button type="button" class="scv2-btn-primary" (click)="addZone()">
      <i class="pi pi-plus"></i> Add zone
    </button>
  </div>

  <!-- loading skeleton -->
  <div *ngIf="loading" class="scv2-zone">
    <div class="scv2-zone-head">
      <p-skeleton width="10rem" height="1.25rem"></p-skeleton>
      <p-skeleton width="3rem" height="1.5rem" styleClass="ml-auto"></p-skeleton>
    </div>
    <div class="scv2-zone-body">
      <div class="scv2-grid-2">
        <div class="scv2-field"><p-skeleton height="3.2rem"></p-skeleton></div>
        <div class="scv2-field"><p-skeleton height="3.2rem"></p-skeleton></div>
      </div>
      <div class="scv2-field"><p-skeleton height="3.2rem"></p-skeleton></div>
      <div class="scv2-field"><p-skeleton height="2.5rem"></p-skeleton></div>
      <div class="scv2-field"><p-skeleton height="3.2rem"></p-skeleton></div>
    </div>
  </div>

  <!-- empty state -->
  <div *ngIf="!loading && zones.length === 0" class="scv2-card scv2-empty">
    <i class="pi pi-map-marker"></i>
    <p class="scv2-empty-title">No shipping zones yet</p>
    <p class="scv2-empty-sub">Add a zone to set how much you charge customers in a region.</p>
    <button type="button" class="scv2-btn-primary" (click)="addZone()">
      <i class="pi pi-plus"></i> Add your first zone
    </button>
  </div>

  <!-- zone cards -->
  <div class="scv2-zone" *ngFor="let zone of zones; let i = index">
    <!-- header (always visible) -->
    <div class="scv2-zone-head">
      <button type="button" class="scv2-zone-toggle" (click)="toggleExpand(zone)">
        <i class="pi pi-chevron-down" [class.scv2-rotated]="zone.expanded"></i>
        <span class="scv2-zone-name">{{ zone.zoneName }}</span>
        <span *ngIf="!zone.expanded" class="scv2-zone-summary">{{ zoneSummary(zone) }}</span>
      </button>
      <app-toggle-v2 [checked]="zone.isEnabled" (checkedChange)="zone.isEnabled = $event"></app-toggle-v2>
      <button type="button" class="scv2-zone-del" (click)="removeZone(zone)" aria-label="Delete zone">
        <i class="pi pi-trash"></i>
      </button>
    </div>

    <!-- body -->
    <div class="scv2-zone-body" *ngIf="zone.expanded">
      <div class="scv2-grid-2">
        <div class="scv2-field">
          <label>Zone name</label>
          <input class="scv2-input" [class.scv2-input-error]="submitted && zoneNameError(zone)" [(ngModel)]="zone.zoneName" placeholder="Zone name" />
          <p class="scv2-field-error" *ngIf="submitted && zoneNameError(zone)">{{ zoneNameError(zone) }}</p>
        </div>
        <div class="scv2-field">
          <label>Country / Region</label>
          <div class="scv2-select-wrap">
            <select class="scv2-select" title="Country or region" [(ngModel)]="zone.countries[0]">
              <option value="USA">USA</option>
            </select>
            <i class="pi pi-chevron-down"></i>
          </div>
        </div>
      </div>

      <!-- states -->
      <div class="scv2-field">
        <label>States</label>
        <p-multiSelect
          [options]="usStates"
          [(ngModel)]="zone.states"
          optionLabel="name"
          optionValue="code"
          [maxSelectedLabels]="3"
          selectedItemsLabel="{0} states selected"
          placeholder="Select states (or leave blank for all)"
          styleClass="scv2-states-select">
        </p-multiSelect>
        <p class="scv2-field-hint">Leave blank to cover the whole country.</p>
      </div>

      <!-- rate type -->
      <div class="scv2-field">
        <label>Shipping rate type <span class="scv2-req">*</span></label>
        <div class="scv2-ratetype-grid">
          <button
            type="button"
            *ngFor="let opt of rateTypes"
            class="scv2-ratetype-btn"
            [class.scv2-active]="zone.rateType === opt.id"
            (click)="zone.rateType = opt.id"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <!-- FLAT -->
      <div class="scv2-field" *ngIf="zone.rateType === 'flat'">
        <label>Shipping cost (USD)</label>
        <div class="scv2-money">
          <span>$</span>
          <input class="scv2-input scv2-money-input" [class.scv2-input-error]="submitted && flatCostError(zone)" type="number" min="0" title="Flat shipping cost" placeholder="0.00" [(ngModel)]="zone.flatCost" />
        </div>
        <p class="scv2-field-error" *ngIf="submitted && flatCostError(zone)">{{ flatCostError(zone) }}</p>
      </div>

      <!-- FREE -->
      <div class="scv2-free-msg" *ngIf="zone.rateType === 'free'">
        Customers in this zone always see Free shipping at checkout.
      </div>

      <!-- LIVE (USPS only for now) — no carrier-connection requirement (V1 parity) -->
      <ng-container *ngIf="zone.rateType === 'live'">
        <div class="scv2-rule-carrier" *ngFor="let id of carrierIds">
          <div class="scv2-rule-carrier-head">
            <app-carrier-mark-v2 [carrierId]="id" [size]="32"></app-carrier-mark-v2>
            <div class="scv2-rule-carrier-main">
              <div class="scv2-rule-carrier-name">{{ carriers[id].name }}</div>
              <div class="scv2-rule-carrier-sub">{{ carrierSubtitle(id) }}</div>
            </div>
            <!-- Enable USPS Rates toggle (parity with V1) -->
            <app-toggle-v2
              *ngIf="id === 'usps'"
              [checked]="zone.uspsEnabled"
              (checkedChange)="zone.uspsEnabled = $event"
            ></app-toggle-v2>
          </div>
          <!-- USPS service chips -->
          <div class="scv2-rule-services" *ngIf="id === 'usps' && zone.uspsEnabled">
            <button
              type="button"
              *ngFor="let svc of uspsServices"
              class="scv2-chip"
              [class.scv2-active]="isUspsServiceActive(zone, svc.value)"
              (click)="toggleUspsService(zone, svc.value)"
            >
              {{ svc.label }}
            </button>
          </div>
          <p class="scv2-field-error scv2-rule-services-error" *ngIf="id === 'usps' && zone.uspsEnabled && submitted && uspsServicesError(zone)">
            <i class="pi pi-exclamation-circle"></i> {{ uspsServicesError(zone) }}
          </p>
        </div>

        <!-- TODO re-enable: handling fee + fallback (multi-carrier live rates). Hidden while USPS-only. -->
        <div class="scv2-grid-2 scv2-fees" *ngIf="upsEnabled">
          <div class="scv2-field">
            <label>Handling fee added to live rate</label>
            <div class="scv2-money">
              <span>$</span>
              <input class="scv2-input scv2-money-input" [(ngModel)]="zone.markup" />
            </div>
            <p class="scv2-field-hint">Added on top of the carrier's quoted price.</p>
          </div>
          <div class="scv2-field">
            <label>Fallback flat rate</label>
            <div class="scv2-money">
              <span>$</span>
              <input class="scv2-input scv2-money-input" [(ngModel)]="zone.fallback" />
            </div>
            <p class="scv2-field-hint">Used if a live rate request fails or times out.</p>
          </div>
        </div>
      </ng-container>

      <!-- store pickup -->
      <div class="scv2-field scv2-pickup">
        <label>Enable store pickup?</label>
        <app-toggle-v2 [checked]="zone.pickup" (checkedChange)="zone.pickup = $event"></app-toggle-v2>
      </div>
      <div class="scv2-field" *ngIf="zone.pickup">
        <label>Pickup cost (USD)</label>
        <div class="scv2-money">
          <span>$</span>
          <input class="scv2-input scv2-money-input" type="number" min="0" title="Store pickup cost" placeholder="0.00" [(ngModel)]="zone.pickupCost" />
        </div>
      </div>
    </div>
  </div>

  <!-- save -->
  <div class="scv2-rules-actions" *ngIf="zones.length > 0">
    <button type="button" class="scv2-btn-primary" [disabled]="saving" (click)="save()">
      <i class="pi pi-check"></i> {{ saving ? 'Saving…' : 'Save shipping settings' }}
    </button>
  </div>
</div>
